"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useMemo, useState, useEffect, useRef } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { useWave } from "@/hooks/useWave";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { createBreakpoint } from "react-use";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const useBreakpoint = createBreakpoint({
  MD: 768,
  S: 0
});
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(39);
  const {
    wave,
    onDropClick
  } = t0;
  const {
    isMemesWave
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const breakpoint = useBreakpoint();
  const mountedRef = useRef(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      mountedRef.current = false;
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [isCreatingDrop, setIsCreatingDrop] = useState(false);
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, _temp);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, _temp2);
  let t3;
  bb0: {
    if (breakpoint === "S") {
      t3 = "list";
      break bb0;
    }
    t3 = viewMode;
  }
  const effectiveViewMode = t3;
  let t4;
  if ($[2] !== wave) {
    t4 = <div className="tw-pt-2 md:tw-pt-4"><WaveLeaderboardTime wave={wave} /></div>;
    $[2] = wave;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== setViewMode) {
    t5 = mode => setViewMode(mode);
    $[4] = setViewMode;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (mountedRef.current) {
        setIsCreatingDrop(true);
      }
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== setSort) {
    t7 = s => setSort(s);
    $[7] = setSort;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== effectiveViewMode || $[10] !== sort || $[11] !== t5 || $[12] !== t7 || $[13] !== wave) {
    t8 = <div className="tw-sticky tw-top-0 tw-z-10 tw-bg-black"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t5} onCreateDrop={t6} onSortChange={t7} /></div>;
    $[9] = effectiveViewMode;
    $[10] = sort;
    $[11] = t5;
    $[12] = t7;
    $[13] = wave;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== isCreatingDrop || $[16] !== isMemesWave || $[17] !== wave) {
    t9 = isCreatingDrop && !isMemesWave && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreatingDrop(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreatingDrop(false);
        }
      }} /></motion.div>;
    $[15] = isCreatingDrop;
    $[16] = isMemesWave;
    $[17] = wave;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <AnimatePresence>{t9}</AnimatePresence>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== isCreatingDrop || $[22] !== isMemesWave || $[23] !== wave) {
    t11 = isMemesWave && isCreatingDrop && <MemesArtSubmissionModal isOpen={isCreatingDrop} wave={wave} onClose={() => setIsCreatingDrop(false)} />;
    $[21] = isCreatingDrop;
    $[22] = isMemesWave;
    $[23] = wave;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== effectiveViewMode || $[26] !== onDropClick || $[27] !== sort || $[28] !== wave) {
    t12 = effectiveViewMode === "list" ? <WaveLeaderboardDrops wave={wave} sort={sort} onCreateDrop={() => {
      if (mountedRef.current) {
        setIsCreatingDrop(true);
      }
    }} /> : <WaveLeaderboardGallery wave={wave} sort={sort} onDropClick={onDropClick} />;
    $[25] = effectiveViewMode;
    $[26] = onDropClick;
    $[27] = sort;
    $[28] = wave;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t10 || $[31] !== t11 || $[32] !== t12) {
    t13 = <div className="tw-mt-2">{t10}{t11}{t12}</div>;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== leaderboardViewStyle || $[35] !== t13 || $[36] !== t4 || $[37] !== t8) {
    t14 = <div className="tw-w-full tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t4}{t8}{t13}</div>;
    $[34] = leaderboardViewStyle;
    $[35] = t13;
    $[36] = t4;
    $[37] = t8;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  return t14;
};
export default MyStreamWaveLeaderboard;
function _temp(value) {
  return value === "list" || value === "grid";
}
function _temp2(value_0) {
  return Object.values(WaveDropsLeaderboardSort).includes(value_0);
}